/** Question Screen Component */

import React from 'react';
import { IonButton, IonIcon, IonLabel, IonSegment, IonSegmentButton, IonReorderGroup, IonReorder, IonItem, IonImg } from '@ionic/react';
import { ItemReorderEventDetail } from '@ionic/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import * as curlyquotes from 'curlyquotes';
import { decodeText } from '../ts/textDecode';
import '../theme/Question.css';

import withApplicationState from '../store';
import AdMobFuncs from '../ts/adMob';
import { QuestionData, ApplicationState } from '../ts/interfaces';
import { ENVIRONMENT } from '../ts/environment';
//import { Preferences } from '@capacitor/preferences';
import { Howl } from 'howler';

import reorderIcon from '../assets/icons/ui/reorder.svg';
import { correctResponseActionTypes, countCorrectActionTypes, lastQTextActionTypes, lastResponseActionTypes, submittedActionTypes } from '../ts/actionTypes';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
//import { convertCompilerOptionsFromJson } from 'typescript';

/**
 * Helper function to process formatting for language names
 */
function formatLangName (str: string) {
  // Remove Hyphens
  str = str.replace('-', ' ');

  // Capitalize all first letters
  let words = str.split(' ');
  words.forEach((word, i) => {
    if ((word !== "language") && (word !== "the")) {
      words[i] = word[0].toUpperCase() + word.slice(1);
    }
  });

  return words.join(' ');
}

/**
 * Format year display: negative -> positive + ' BCE', otherwise as-is
 */
function formatYearDisplay (d: any): string {
  const num = Number(d);
  if (!Number.isFinite(num)) return String(d ?? '');
  if (num < 0) return `${Math.abs(num)} BCE`;
  const int = Math.trunc(num);
  return (int >= 10 && int <= 999) ? `${int} CE` : String(int);
}

/* Wrap a phrase in straight double quotes unless it's already quoted (straight or curly). */
function quoteIfNeeded (s?: string): string {
  const t = (s ?? '').trim();
  if (!t) return '""';
  const pairs: Array<[string, string]> = [['"', '"'], ['“','”'], ['‘','’'], ["'","'"]];
  for (const [L, R] of pairs) {
    if (t.startsWith(L) && t.endsWith(R)) return t;
  }
  return `"${t}"`;
}

interface QuestionProps {
  store:             ApplicationState;
  qReview:            boolean;
  dispatch:          ({ type }: { type: string; payload?: any; }) => void;
};

interface QuestionState {
  selectedAnswer:   string       | undefined;
  currentOrder:     Array<string | undefined>;
}

class Question extends React.Component<QuestionProps, QuestionState> {
  private _questionData: QuestionData;
  private _timeInitial: number;

  constructor(props: QuestionProps) {
    super(props);

    const { currentQuestionsArray, currentQuestionIndex } = this.props.store;
    this._questionData = currentQuestionsArray[currentQuestionIndex!];
    this._timeInitial = + new Date().getTime();

    this.state = {
      selectedAnswer:   undefined,
      currentOrder:     currentQuestionsArray[currentQuestionIndex!].options,
    };
  }

  
  componentDidMount() {
    const { platform } = this.props.store;
    let { options } = this._questionData;
    this.setState({currentOrder: options});

    // Store question text in global state so we can send it with analytics event
    this.props.dispatch({
      type: lastQTextActionTypes.SET_TEXT,
      payload: this.getQuestionText()
    });
  }

  /**
   *  Returns question prompt text according to question type
   */
  private getQuestionText () {
      const { type, category, subject, subjectLangs, range, article, subcat, author } = this._questionData;
      const { currentDifficulty } = this.props.store;

      if (type === "category-include") {
        // Text for category-include questions
        switch (category) {
          case "brands" :
            return "Choose the phrase that HAS been used as a brand name:";
          case "sports" :
            return "Choose the phrase that HAS been used as sports terminology:";
          case "money" :
            return "Choose the phrase that HAS been used as financial terminology:";
          case "arts" :
            return "Choose the phrase that HAS been used as terminology within the arts:";
          case "imitative" :
            return "Choose the phrase that HAS been used as an imitation of a sound:";
          case "food" :
            return "Choose the phrase that HAS been used in relation to food or drink:";
          case "imported" :
              return "Choose the phrase that WAS imported into English:";
          default : 
            return "Invalid Category";
        }
      } else if (type === "category-exclude") {
        // Text for category-exclude questions
        switch (category) {
          case "brands" :
            return "Choose the phrase that has NOT been used as a brand name:";
          case "sports" :
            return "Choose the phrase that did NOT originate as sports terminology:";
          case "money" :
            return "Choose the phrase that did NOT originate as financial terminology:";
          case "arts" :
            return "Choose the phrase that did NOT originate as terminology within the arts:";
          case "imitative" :
            return "Choose the phrase that did NOT originate as an imitation of a sound:";
          case "food" :
            return "Choose the phrase that has NOT been used in relation to food or drink:";
          case "imported" :
              return "Choose the phrase that was NOT imported into English:";
          default :
            return "Invalid Category";
        }
      } else if (type === "origin-include" && subjectLangs) {
        // Text for origin-include questions
        subjectLangs.forEach((lang, i) => subjectLangs[i] = formatLangName(lang));
        switch (subjectLangs.length) {
          case 1: 
            return "Which phrase comes from " + subjectLangs[0] + "?";
          case 2:
            return "Which phrase comes from " + subjectLangs[0] + " and " + subjectLangs[1] + "?";
          default:
            return "Which phrase comes from languages including " + subjectLangs.slice(0, subjectLangs.length - 1).join(', ') + ", and " + subjectLangs[subjectLangs.length - 1] + "?";
        }
      } else if (type === "origin-exclude" && subjectLangs) {
        // Text for origin-exclude questions
        subjectLangs.forEach((lang, i) => subjectLangs[i] = formatLangName(lang));
        switch (subjectLangs.length) {
          case 1: 
            return "Which phrase does NOT come from " + subjectLangs[0] + "?";
          case 2:
            return "Which phrase does NOT come from " + subjectLangs[0] + " and " + subjectLangs[1] + "?";
          default:
            return "Which phrase does NOT come from languages including " + subjectLangs.slice(0, subjectLangs.length - 1).join(', ') + ", and " + subjectLangs[subjectLangs.length - 1] + "?";
        }
      } else if (type === "origin-multi") {
        // Text for origin-multi questions
        switch (currentDifficulty) {
          case "moderate":
            return "Which two languages does " + quoteIfNeeded(subject) + " come from?";
          case "hard": {
            const quoted = quoteIfNeeded(subject);
            const skipQ = /[\?\uFF1F]\s*$/.test(decodeText(subject || "")) ? "" : "?";
            return "From which of these languages does modern English get the phrase " + quoted + skipQ;
          }
          default:
            return "Which language does " + quoteIfNeeded(subject) + " come from?";
        }
      } else if (type === "subcat-include") {
        // Text for subcat-include questions
        if (subcat === "advertising") {
          return "Which of these phrases HAS been used in advertising?";
        } else if (subcat === "bible") {
          return "Which of these phrases HAS been used in relation to the Bible?";
        } else if (subcat === "book") {
          return "Which of these phrases HAS been used in relation to a book?";
        } else if (subcat === "device") {
          return "Which of these phrases HAS been used in relation to a device?";
        } else if (subcat === "letter") {
          return "Which of these phrases HAS been used in relation to a letter?";
        } else if (subcat === "magazine-newspaper") {
          return "Which of these phrases HAS been used in magazine/newspaper?";
        } else if (subcat === "movies-tv") {
          return "Which of these phrases HAS been used in Movies/TV?";
        } else if (subcat === "music") {
          return "Which of these phrases HAS been used in music vernacular?";
        } else if (subcat === "place") {
          return "Which of these phrases HAS been used in relation to a place name?";
        } else if (subcat === "speech") {
          return "Which of these phrases HAS been used in speech?";
        } else if (subcat === "sports") {
          return "Which of these phrases HAS been used in sports?";
        } else if (subcat === "theater") {
          return "Which of these phrases HAS been used in theater?";
        } else if (article) {
          return "Which of these phrases HAS been used in relation to " + article.slice(1, -1) + " " +  subcat + "?";
        } else {
          return "Which of these phrases HAS been used in " +  subcat + "?";
        }
      } else if (type === "subcat-exclude") {
        // Text for subcat-include questions
        if (subcat === "advertising") {
          return "Which of these phrases has NOT been used in advertising?";
        } else if (subcat === "bible") {
          return "Which of these phrases has NOT been used in relation to the Bible?";
        } else if (subcat === "book") {
          return "Which of these phrases has NOT been used in relation to a book?";
        } else if (subcat === "device") {
          return "Which of these phrases has NOT been used in relation to a device?";
        } else if (subcat === "letter") {
          return "Which of these phrases has NOT been used in relation to a letter?";
        } else if (subcat === "magazine-newspaper") {
          return "Which of these phrases has NOT been used in magazine/newspaper?";
        } else if (subcat === "movies-tv") {
          return "Which of these phrases has NOT been used in Movies/TV?";
        } else if (subcat === "music") {
          return "Which of these phrases has NOT been used in music vernacular?";
        } else if (subcat === "place") {
          return "Which of these phrases has NOT been used in relation to a place name?";
        } else if (subcat === "speech") {
          return "Which of these phrases has NOT been used in speech?";
        } else if (subcat === "sports") {
          return "Which of these phrases has NOT been used in sports?";
        } else if (subcat === "theater") {
          return "Which of these phrases has NOT been used in theater?";
        } else if (article) {
          return "Which of these phrases has NOT been used in relation to " + article.slice(1, -1) + " " +  subcat + "?";
        } else {
          return "Which of these phrases has NOT been used in " +  subcat + "?";
        }
      } else {
        // Prefix "the" when options will be decades or centuries e.g. "the 1910s" or "the 19th century"
        let rangePrefix = '';

        if (range?.endsWith('century')) {
          rangePrefix = 'the ';
        }

        // All other question types
        switch (type) {
          case "date" :
            return "When is the phrase " + quoteIfNeeded(subject) + " first recorded in English?";
          case "date-range-include" :
            return "Which of the following phrases IS first recorded in " + rangePrefix + range + "?";
          case "date-range-exclude" :
            return "Which of the following phrases is NOT first recorded in " + rangePrefix + range + "?";
          case "order" :
            return "Order the following phrases descending by date of first use (First used phrase on top):";
          case "first" :
            return "Which of the following phrases WAS the first to appear in English?";
          case "last" :
            return "Which of the following phrases WAS the last to appear in English?";
          case "subcat-multi" :
            return "The phrase " + quoteIfNeeded(subject) + " comes from: ";
          case "author-include" :
            return "Which of these phrases is coined by " + author + "?";
          case "author-exclude" :
            return "Which of these phrases is NOT coined by " + author + "?";
          case "author-origin" : {
            const quoted = quoteIfNeeded(subject);
            const skipQ = /[\?\uFF1F]\s*$/.test(decodeText(subject || "")) ? "" : "?";
            return "Who coined the phrase " + quoted + skipQ;
          }
        default:
          return "Invalid question type: " + type
      }
    }
  }

  /**
   * Change state when user selects an answer
   * @param val value of selected answer
   */
  setSelectedAnswer(val: string | undefined) {
    const { platform, soundsOn, uiAudio, vibrationOn, focused } = this.props.store;

    this.setState({ selectedAnswer: val });
    if (focused && soundsOn) {
      const uiPopAudio: Howl = new Howl({
        src: ['assets/pop.mp3'],
        format: ['mp3'],
        volume: 0.3,
        onloaderror: (id, e) => console.log('failed: ' + id + ' msg: ' + e)
    });
    uiPopAudio.play();
    } else {
      if (soundsOn) uiAudio.play();
    }
    if (platform !== 'web' && vibrationOn) Haptics.impact({ style: ImpactStyle.Light });
  }

  /**
   * Required procedure to change state on reorder event
   * @param event reorder event
   */
  async doReorder(event: CustomEvent<ItemReorderEventDetail>) {
    let { currentOrder } = this.state;

    let from = event.detail.from;
    let to = event.detail.to;
    let newOrder = [...currentOrder];

    // Remove element at index and splice it into the new index
    newOrder.splice(from, 1)
    newOrder.splice(to, 0, currentOrder[from]);

    this.setState({currentOrder: newOrder});
    event.detail.complete();
  }

  /**
   * Procedure to check if submitted answer is correct upon submission
   * @param ans submitted answer
   */
  answerCheck(ans: string | Array<string | undefined>) {
    const { currentQuestionsArray, currentQuestionIndex, platform, soundsOn, vibrationOn, uiAudio, focused } = this.props.store;
    const { type, answer, subject } = this._questionData;

    // Set result as correct answer, try to invalidate
    let correct = true;

    if (focused && soundsOn) {
      const uiPopAudio: Howl = new Howl({
        src: ['assets/pop.mp3'],
        format: ['mp3'],
        volume: 0.3,
        onloaderror: (id, e) => console.log('failed: ' + id + ' msg: ' + e)
    });
    uiPopAudio.play();
    } else {
      if (soundsOn) uiAudio.play();
    }
    
    if (platform !== 'web' && vibrationOn) Haptics.impact({style: ImpactStyle.Light});

    if (currentQuestionsArray.length > 0 && 
        currentQuestionIndex  !== undefined) {

      if (type === 'order') {
          // If array, then cast var and check with loop
          let ansOrder = ans as Array<string>;
          ansOrder.forEach((element, i) => {
            if (answer[i] !== element) {
              correct = false;
            }
          });
      } else if (ans !== answer) {
        correct = false;
      }

      //console.log('GAME UUID IN QUESTION ANSWERED');
      //console.log(this.props.store.gameUuid);

      // If answer is a specific word, log event
      if (ENVIRONMENT === 'production') {
        if (subject) {
          let timeFinal = + new Date().getTime();
          FirebaseAnalytics.logEvent({
            name: 'question_answered',
            params: {
              game_uuid: this.props.store.gameUuid,
              subject_word: subject,
              subject_word_diff: this._questionData.subjectDiff,
              question_text: this.getQuestionText(),
              question_type: this._questionData.type,
              question_time_msec: timeFinal - this._timeInitial,
              answered_correctly: correct,
              correct_answer: answer
            }
          });
        }
      }

      //console.log(answer);
      
      this.props.dispatch({
        type: lastResponseActionTypes.SET_RESPONSE,
        payload: ans
      });

      this.props.dispatch({
        type: submittedActionTypes.SET_SUBMITTED
      });

      if (correct) {
        this.props.dispatch({
          type: correctResponseActionTypes.SET_CORRECT
        });

        this.props.dispatch({
          type: countCorrectActionTypes.INCREMENT_COUNT
        })
      } else {
        this.props.dispatch({
          type: correctResponseActionTypes.SET_INCORRECT
        });
      }
    }
  }
  
  render() {
    const { type,reviewImgURL, answer, optionsDates } = this._questionData;
    var { options } = this._questionData;
    const { selectedAnswer, currentOrder } = this.state;
    const { qReview } = this.props;
    const { currentDifficulty } = this.props.store;

    // Use current order state as answer when question is an order question
    // Otherwise disable submit button until an answer is selected
    let submitButton = <IonButton disabled>SUBMIT</IonButton>
    if (type === 'order') {
      submitButton = <IonButton disabled={false} onClick={() => this.answerCheck(currentOrder)}>SUBMIT</IonButton>
    } else if (selectedAnswer) {
      submitButton = <IonButton disabled={false} onClick={() => this.answerCheck(selectedAnswer)}>SUBMIT</IonButton>
    }

    if (qReview) {
      submitButton = <></>
    }

    // Sort date options
    if (type === 'date') {
      options.sort();
    }

    var qWrapperClass = "question-wrapper " + currentDifficulty;

    var questionText = curlyquotes.string(decodeText(this.getQuestionText()));
    var questionTextLength = questionText.length;
    var questionTextClass = ''
    if (questionTextLength <= 60) questionTextClass = 'question-header';
    if (questionTextLength > 60) questionTextClass = 'question-header med';
    if (questionTextLength > 70) questionTextClass = 'question-header long';
    if (questionTextLength > 80) questionTextClass = 'question-header extra-long';

    // Determine a single text-length class for all options in this question
    const longestOptionClass = (() => {
      const maxLen = Math.max(
        0,
        ...options.map(o => (o ? decodeText(o) : '').length)
      );
      let cls = 'short';
      if (maxLen > 20) cls = 'med';
      if (maxLen > 27) cls = 'long';
      if (maxLen > 33) cls = 'extra-long';
      if (maxLen > 33) cls = 'super-long';
      return cls;
    })();

    return (
      <div className={qWrapperClass}>
        
        <div className={questionTextClass}>
          <p>
            { questionText }
          </p>
        </div>
        { (type === 'order') ? 
            typeof answer === "object" && qReview && optionsDates? 
            // REORDER QUESTIONS QREVIEW
            <IonSegment mode="md" value={selectedAnswer} onIonChange={e => this.setSelectedAnswer(String(e.detail.value))}>
                {answer.map((option, i) => {
                    const optDisplay = option ? decodeText(option) : '';
                    const text = optDisplay.toUpperCase();
                    return(
                    <IonSegmentButton disabled className='qreview-button' value={option} key={i}>
                      <IonLabel className={longestOptionClass}>
                        {(() => {
                          const raw = (optionsDates && optionsDates[option]) ? optionsDates[option] : "";
                          const date = formatYearDisplay(raw);
                          return (
                            <div className="qreview-reorder-option">
                              <span className="qreview-reorder-text">{text}</span>
                              <span className="qreview-reorder-date">{date}</span>
                            </div>
                          );
                        })()}
                      </IonLabel>
                    </IonSegmentButton>
                );
              })
            }
          </IonSegment>
            :         
            // REORDER QUESTIONS
            <IonReorderGroup disabled={false} onIonItemReorder={e => this.doReorder(e)}>
  
              {options.map((option, i) => {
                  const optDisplay = option ? decodeText(option) : '';
                  let optionTextLength = "reorder-label " + longestOptionClass;
                  return(
                    <IonReorder slot='start' key={i}>
                      <IonItem className='ion-text-center' lines='none'>
                        <div className='item-wrapper'>
                          <IonIcon icon={reorderIcon} />
                          <IonLabel className={optionTextLength} mode="md" position='stacked'>{optDisplay.toUpperCase()}</IonLabel>
                        </div>
                      </IonItem>
                    </IonReorder>
                  );
                })
              }
            </IonReorderGroup>
            : // SINGLE-SELECT QUESTIONS
            <IonSegment mode="md" value={selectedAnswer} onIonChange={e => this.setSelectedAnswer(String(e.detail.value))}>
              { options.map((option, i) => {
                  const optDisplay = option ? decodeText(option) : '';
                  let optionTextLength = longestOptionClass;
                  let buttonClass = type === 'date' ? 'date-button-text' : '';
                  if (qReview) {
                    buttonClass += ' qreview-button';
                    if ( option === answer) {
                      buttonClass += ' segment-button-checked';
                    }
                  } 

                  let optionsButtons = 
                  qReview ?
                    <IonSegmentButton disabled className={buttonClass} value={option} key={i}>
                      <IonLabel className={optionTextLength}>{optDisplay}</IonLabel>
                    </IonSegmentButton>
                    :
                    <IonSegmentButton className={buttonClass} value={option} key={i}>
                      <IonLabel className={optionTextLength}>{optDisplay}</IonLabel>
                    </IonSegmentButton>
                  return(
                    optionsButtons
                  );
                })
              }
            </IonSegment>
        }
        <IonImg style={{visibility: 'hidden', height: '0'}} src={reviewImgURL}/>
        {submitButton}
      </div>
    )
  }
};

export default withApplicationState(Question);